import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { CaseDetail, Team } from '@/api/types';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Notice,
  Page,
  PageHead,
  Tag,
  Segmented,
  TextArea,
} from '@/components/ui';
import { formatDateTime, humanise } from '@/lib/format';
import { slaTone } from '@/routes/Feedback';

const NEXT_STATE: Record<string, { target: string; label: string } | undefined> = {
  received: { target: 'acknowledged', label: 'Acknowledge' },
  acknowledged: { target: 'in_review', label: 'Move to in review' },
};

/** ADM-04 in full: assign, advance, document, reply, resolve. */
export function FeedbackCasePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { data, error, loading, reload } = useApi<CaseDetail>(`/admin/feedback/${caseId}`);
  const team = useApi<Team>('/admin/team');

  const [note, setNote] = useState('');
  const [audience, setAudience] = useState<'internal' | 'family'>('internal');
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;
  if (!data) return null;

  const detail = data.case;
  const resolved = detail.state === 'resolved';
  const next = NEXT_STATE[detail.state];
  const sla = slaTone(detail.sla_state);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      reload();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const admins = (team.data?.users ?? []).filter(
    (member) => member.role === 'admin' && member.status === 'active',
  );

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/feedback">Feedback</Link>
            <span aria-hidden="true">/</span>
            <span>{detail.subject}</span>
          </nav>
        }
        title={detail.subject}
        subtitle={
          detail.recipient_name ? `About ${detail.recipient_name}` : 'No client linked'
        }
        actions={
          <div className="row">
            <Tag
              label={humanise(detail.type)}
              tone={detail.type === 'urgent_concern' ? 'urgent' : 'info'}
            />
            <Tag label={humanise(detail.state)} tone={resolved ? 'positive' : 'attention'} />
            {!resolved ? <Tag label={sla.label} tone={sla.tone} /> : null}
          </div>
        }
      />

      <div className="stack">
        {problem ? (
          <Notice tone="urgent" title="Could not complete that">
            {problem}
          </Notice>
        ) : null}

        {detail.type === 'urgent_concern' && !resolved ? (
          <Notice tone="urgent" title="Urgent concern">
            The family flagged this for immediate attention.
          </Notice>
        ) : null}

        <Card>
          <CardHead title="What the family sent" />
          <div className="card__body">
            <p style={{ whiteSpace: 'pre-line' }}>{detail.body}</p>
            <div className="divider" />
            <p className="meta">
              Received {formatDateTime(detail.created_at)}
              {detail.acknowledge_due_at
                ? ` · acknowledgment target ${formatDateTime(detail.acknowledge_due_at)}`
                : ' · your agency has not set a target for this type'}
            </p>
          </div>
        </Card>

        {!resolved ? (
          <div className="grid-2">
            <Card>
              <CardHead title="Ownership" />
              <div className="card__body stack">
                <p className="meta">
                  {detail.owner_name
                    ? `Owned by ${detail.owner_name}.`
                    : 'Nobody owns this case yet.'}
                </p>
                {admins.length === 0 ? (
                  <p className="meta">No active administrators available.</p>
                ) : (
                  <div className="row row--wrap">
                    {admins.map((member) => (
                      <Button
                        key={member.id}
                        variant={detail.owner_id === member.id ? 'primary' : 'secondary'}
                        onClick={() =>
                          run(() => api.post(`/admin/feedback/${caseId}/assign?owner_id=${member.id}`))
                        }
                        busy={busy}
                      >
                        {member.full_name}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card>
              <CardHead title="Progress" />
              <div className="card__body stack">
                <p className="meta">
                  received → acknowledged → in review → resolved. A case cannot skip
                  acknowledgment.
                </p>
                {next ? (
                  <Button
                    onClick={() =>
                      run(() =>
                        api.post(`/admin/feedback/${caseId}/transition?target_state=${next.target}`),
                      )
                    }
                    busy={busy}
                  >
                    {next.label}
                  </Button>
                ) : (
                  <p className="meta">Ready to resolve once an outcome is recorded.</p>
                )}
              </div>
            </Card>
          </div>
        ) : null}

        {!resolved ? (
          <Card>
            <CardHead title="Document an action" />
            <div className="card__body stack">
              <Segmented
                label="Who sees this"
                options={[
                  { value: 'internal' as const, label: 'Internal note' },
                  { value: 'family' as const, label: 'Reply to the family' },
                ]}
                value={audience}
                onChange={setAudience}
              />
              <Field
                label={audience === 'family' ? 'Your reply' : 'What you did'}
                hint={
                  audience === 'family'
                    ? 'The family reads this in their app, and it appears on their case timeline.'
                    : 'Recorded on the case. The family does not see it.'
                }
              >
                {(props) => (
                  <TextArea {...props} value={note} onChange={(e) => setNote(e.target.value)} />
                )}
              </Field>
              <Button
                onClick={() =>
                  run(async () => {
                    await api.post(`/admin/feedback/${caseId}/actions`, {
                      body: note.trim(),
                      visible_to_family: audience === 'family',
                    });
                    setNote('');
                  })
                }
                busy={busy}
                disabled={!note.trim()}
              >
                {audience === 'family' ? 'Send reply' : 'Save note'}
              </Button>
            </div>
          </Card>
        ) : null}

        {!resolved ? (
          <Card>
            <CardHead title="Close the case" />
            <div className="card__body stack">
              <Field
                label="Resolution note"
                hint="Required. The family sees this."
                error={resolution.trim() ? null : 'A resolution note is required'}
              >
                {(props) => (
                  <TextArea
                    {...props}
                    value={resolution}
                    onChange={(event) => setResolution(event.target.value)}
                    placeholder="What was done, and what the family should know"
                  />
                )}
              </Field>
              <Button
                onClick={() =>
                  run(async () => {
                    await api.post(`/admin/feedback/${caseId}/resolve`, {
                      resolution_note: resolution.trim(),
                    });
                    navigate('/feedback');
                  })
                }
                busy={busy}
                disabled={!resolution.trim()}
              >
                Resolve and close
              </Button>
            </div>
          </Card>
        ) : (
          <Notice tone="positive" title="Resolved">
            {detail.resolution_note}
          </Notice>
        )}

        <Card variant="flush">
          <CardHead title="Case history" />
          <div className="card__body stack">
            {data.events.length === 0 ? (
              <p className="meta">No actions recorded yet.</p>
            ) : (
              data.events.map((event, index) => (
                <div key={index}>
                  <div className="row row--between">
                    <span className="strong">{humanise(event.kind)}</span>
                    <div className="row">
                      {event.visible_to_family ? <Tag label="Family saw this" tone="info" /> : null}
                      <span className="meta">
                        {event.actor ?? 'System'} · {formatDateTime(event.created_at)}
                      </span>
                    </div>
                  </div>
                  {event.body ? (
                    <p className="meta" style={{ marginTop: 4, whiteSpace: 'pre-line' }}>
                      {event.body}
                    </p>
                  ) : null}
                  {index < data.events.length - 1 ? <div className="divider" /> : null}
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </Page>
  );
}
